import { get, post, post_with_config } from '@/services/http';
import { StockyRequestError } from './stocky_request_error';

export interface StockyConnectionStatus {
  hasKey: boolean;
  maskedKey?: string;
}

export interface StockyConnectionResult {
  connected: boolean;
}

export interface StockySupplierConflict {
  existingId: number;
  existingName: string;
  existingEmail: string;
}

export interface StockySupplierPreviewItem {
  stockyId: string;
  name: string;
  contactEmail: string;
  contactPhone: string | null;
  address: string | null;
  status: string;
  isNew: boolean;
  conflict: StockySupplierConflict | null;
}

export interface StockySupplierPreview {
  suppliers: StockySupplierPreviewItem[];
  totalCount: number;
  conflictCount: number;
  newCount: number;
}

export interface StockySupplierImportResult {
  suppliers: {
    created: number;
    updated: number;
    skipped: number;
    failed: number;
  };
  taxTypes: {
    created: number;
    skipped: number;
    failed: number;
  };
  errors: string[];
}

export interface StockyPOPreviewItem {
  stockyId: number;
  poNumber: string;
  supplierName: string | null;
  supplierMatched: boolean;
  status: string;
  orderDate: string | null;
  itemCount: number;
  matchedItems: number;
  unmatchedItems: number;
  totalCost: number;
  isDuplicate: boolean;
}

export interface StockyPOPreview {
  purchaseOrders: StockyPOPreviewItem[];
  summary: {
    totalPOs: number;
    totalItems: number;
    matchedItems: number;
    unmatchedItems: number;
    matchRate: number;
  };
}

export interface StockyPOImportJob {
  jobId: string;
  status: string;
}

/**
 * What a re-import does with a PO the merchant has received against or edited
 * in Assisty since it was first imported.
 *
 * 'skip'      — Assisty is the source of truth; the PO is left exactly as it is.
 * 'overwrite' — Stocky is the source of truth; the PO is rebuilt wholesale.
 *
 * There is no partial option on purpose. Keeping local receipts while replacing
 * the PO from Stocky would leave the header and the receipts list reporting
 * different received totals, so overwrite is all-or-nothing per PO.
 */
export type StockyConflictMode = 'skip' | 'overwrite';

export interface StockyPOResults {
  /** POs that did not exist in Assisty and were inserted. */
  created: number;
  /** POs that already existed and were refreshed from Stocky. */
  updated: number;
  /**
   * POs deliberately left untouched because the merchant has received against
   * them or edited them in Assisty. Not an error — the migration protects
   * local work rather than overwriting it.
   */
  skippedLocked: number;
  /**
   * POs that were locked but got rebuilt from Stocky anyway, because the run
   * chose Stocky as the source of truth. Counterpart to skippedLocked.
   */
  overwrittenLocked: number;
  /**
   * Returns and adjustments deleted by an overwrite. These exist only in
   * Assisty — Stocky has no equivalent — so they cannot be restored.
   */
  discardedReturns: number;
  discardedAdjustments: number;
  /**
   * Deleted receipts that had already pushed stock to Shopify. The Shopify
   * movement stays; only Assisty's record of it is gone.
   */
  discardedSyncedReceipts: number;
  /** Internal bookkeeping: seen twice in one run, or row vanished mid-run. */
  skipped: number;
  failed: number;
  totalItems: number;
  matchedItems: number;
  unmatchedItems: number;
  totalStockyPOs: number;
  orphansRecovered: number;
  /** Stocky POs we could not account for — the import may be incomplete. */
  unreconciled: number;
  /** Duplicate rows left over from the pre-fix importer, cleaned up on request. */
  preExistingDuplicates: number;
  /**
   * Stocky POs whose number was already taken by an existing Assisty PO, so they
   * were imported under a suffixed number instead.
   *
   * Not a conflict in the skip/overwrite sense: a shared number does not make
   * two POs the same document — identity is Stocky's id — so these are imported
   * alongside the existing PO rather than merged into it.
   */
  renamedOnNumberCollision: number;
  /** Stocky's pagination looked wrong, so the fetch may have missed POs. */
  paginationSuspect: boolean;
  /** Line items whose supplier could not be mapped to an Assisty supplier. */
  suppliersUnresolved: number;
  errors: string[];
}

/**
 * The states the backend actually writes to the job record.
 *
 * `created` is the one that matters most here: the API enqueues the Faktory job
 * and returns immediately, so a job spends everything between enqueue and a
 * worker reserving it in `created` with progress 0. That can be minutes when the
 * queue is busy. Treating it as "not running" leaves the wizard rendering
 * nothing at all, which reads as a silent failure.
 *
 * `not_found` comes back when the job record is gone (Redis eviction, or an id
 * from an old session) — the status endpoint synthesizes it rather than 404ing.
 *
 * This union previously listed a `pending` the backend never sends and omitted
 * both `created` and `not_found`, so comparisons against the real values type-
 * checked as unreachable-but-legal and the gap went unnoticed.
 */
export type StockyPOJobState =
  | 'created'
  | 'processing'
  | 'completed'
  | 'error'
  | 'not_found';

export interface StockyPOJobStatus {
  status: StockyPOJobState;
  phase?: string;
  progress?: number;
  /**
   * Older jobs predate several of these counters, so every field is optional at
   * runtime even though the backend always sends them now. Read through the
   * wizard's helpers rather than indexing directly.
   */
  results?: Partial<StockyPOResults>;
  error?: string;
}

// Re-exported so callers keep importing everything Stocky-related from here.
// Bare `export` (not `export type`) because this is a runtime value.
export { StockyRequestError } from './stocky_request_error';

export interface StockyVariantSupplierPreviewItem {
  name: string;
  matched: boolean;
  assistySupplierId: number | null;
  variantCount: number;
}

export interface StockyVariantAssignmentPreview {
  suppliers: StockyVariantSupplierPreviewItem[];
  summary: {
    totalRows: number;
    totalAssignments: number;
    suppliersFound: number;
    suppliersNotFound: number;
    alreadyLinked: number;
    linkedToOther: number;
    toCreate: number;
  };
}

export interface StockyVariantAssignmentResult {
  created: number;
  skipped: number;
  reassigned: number;
  failed: number;
  errors: Array<{ supplier: string; error: string }>;
}

/**
 * The response body of a failed request, as an object.
 *
 * `http.ts` configures a `transformResponse` that returns the body verbatim
 * instead of parsing it, so `error.response.data` is usually a JSON *string*.
 * Reading a field off it directly silently yields `undefined` — which is how
 * the 409's `jobId` went missing. Parse once here, and read every field from
 * the result.
 */
function errorPayload(error: any): any {
  const data = error?.response?.data;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return null; // not JSON — e.g. an HTML error page from a proxy
    }
  }
  return data ?? null;
}

function extractErrorMessage(error: any, fallback: string): string {
  return errorPayload(error)?.error || fallback;
}

export class StockyMigrationModel {

  public static async getConnection(): Promise<StockyConnectionStatus> {
    const response = await get<StockyConnectionStatus>('/migration/stocky/connection');
    return response;
  }

  public static async connect(stockyApiKey: string): Promise<StockyConnectionResult> {
    try {
      const response = await post<StockyConnectionResult>('/migration/stocky/connect', { stockyApiKey });
      return response;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to connect to Stocky. Please check your API key.'));
    }
  }

  public static async previewSuppliers(): Promise<StockySupplierPreview> {
    try {
      const response = await get<StockySupplierPreview>('/migration/stocky/preview/suppliers');
      return response;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to load supplier preview.'));
    }
  }

  public static async importSuppliers(
    supplierConflictResolutions: Record<number, string>
  ): Promise<StockySupplierImportResult> {
    try {
      const response = await post<StockySupplierImportResult>('/migration/stocky/import/suppliers', {
        supplierConflictResolutions,
      });
      return response;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to import suppliers.'));
    }
  }

  public static async previewPurchaseOrders(statusFilters: string[] = []): Promise<StockyPOPreview> {
    try {
      const params = statusFilters.length > 0 ? `?statusFilters=${statusFilters.join(',')}` : '';
      const response = await get<StockyPOPreview>(`/migration/stocky/preview/purchase-orders${params}`);
      return response;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to load purchase order preview.'));
    }
  }

  public static async importPurchaseOrders(
    statusFilters: string[] = [],
    conflictMode: StockyConflictMode = 'skip',
  ): Promise<StockyPOImportJob> {
    try {
      const response = await post<StockyPOImportJob>('/migration/stocky/import/purchase-orders', {
        statusFilters,
        conflictMode,
      });
      return response;
    } catch (error) {
      // Keep the status: the backend answers 409 when an import is already
      // running for this store, which the wizard shows as information rather
      // than as a failure. A 409 also names that import, so the wizard can
      // attach to it rather than making the merchant come back later.
      throw new StockyRequestError(
        extractErrorMessage(error, 'Failed to start purchase order import.'),
        (error as any)?.response?.status,
        errorPayload(error)?.jobId
      );
    }
  }

  public static async getImportStatus(jobId: string): Promise<StockyPOJobStatus> {
    const response = await get<StockyPOJobStatus>(`/migration/stocky/import/status/${jobId}`);
    return response;
  }

  public static async previewVariantAssignments(file: File): Promise<StockyVariantAssignmentPreview> {
    try {
      const formData = new FormData();
      formData.append('csvFile', file, file.name);
      const response = await post_with_config<StockyVariantAssignmentPreview>(
        '/migration/stocky/preview/variant-assignments',
        formData
      );
      return response;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to preview variant assignments.'));
    }
  }

  public static async importVariantAssignments(
    file: File,
    options: { overrideExisting?: boolean } = {}
  ): Promise<StockyVariantAssignmentResult> {
    try {
      const formData = new FormData();
      formData.append('csvFile', file, file.name);
      formData.append('overrideExisting', (options.overrideExisting ?? false).toString());
      const response = await post_with_config<StockyVariantAssignmentResult>(
        '/migration/stocky/import/variant-assignments',
        formData
      );
      return response;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to import variant assignments.'));
    }
  }
}
